import { ArrowRight } from "lucide-react";
import type { CSSProperties } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Capability, Feature, TextBlock } from "../content/site";
import type { WorkImage } from "../content/workImages";
import { Eyebrow, SectionHeading } from "./SectionHeading";

export function Hero({
  eyebrow,
  title,
  copy,
  primary,
  secondary,
  compact = false,
  images
}: TextBlock & {
  primary?: { label: string; href: string };
  secondary?: { label: string; href: string };
  compact?: boolean;
  images?: readonly WorkImage[];
}) {
  const clientImage = images?.[0];
  const hasSlideshow = Boolean(images && images.length > 1);

  return (
    <section className={`hero section-pad ${compact ? "compact" : ""}`} data-reveal>
      <div
        className={`hero-media ${hasSlideshow ? "hero-media-slideshow" : "hero-media-client"}`}
        aria-label={hasSlideshow ? "Selected client work slideshow" : "Selected client work"}
      >
        <div className="hero-slide-stack">
          {hasSlideshow ? (
            images?.map((image, index) => (
              <figure
                className="hero-slide"
                key={image.id}
                style={{ "--slide-index": index } as CSSProperties}
              >
                <img
                  alt={image.alt}
                  decoding="async"
                  fetchPriority={index === 0 ? "high" : "auto"}
                  height={image.height}
                  src={image.src}
                  width={image.width}
                />
                <figcaption>
                  <strong>{image.title}</strong>
                  <span>{image.description}</span>
                </figcaption>
              </figure>
            ))
          ) : clientImage ? (
            <figure className="hero-work-image">
              <img
                alt={clientImage.alt}
                decoding="async"
                fetchPriority="high"
                height={clientImage.height}
                src={clientImage.src}
                width={clientImage.width}
              />
              <figcaption>
                <strong>{clientImage.title}</strong>
                <span>{clientImage.description}</span>
              </figcaption>
            </figure>
          ) : null}
        </div>
        <div className="hero-media-footer">
          <span>Selected client work</span>
          <span>{hasSlideshow ? `${images?.length} project views` : clientImage?.title}</span>
        </div>
      </div>
      <div className="hero-content">
        <SectionHeading eyebrow={eyebrow} level="h1" title={title} />
        <p>{copy}</p>
        {(primary || secondary) && (
          <div className="actions">
            {primary ? (
              <Link className="button primary" to={primary.href}>
                {primary.label}
                <ArrowRight size={18} />
              </Link>
            ) : null}
            {secondary ? (
              <Link className="button secondary" to={secondary.href}>
                {secondary.label}
              </Link>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}

export function Intro({ eyebrow, title, copy }: TextBlock) {
  return (
    <section className="intro section-pad narrow" data-reveal>
      <SectionHeading eyebrow={eyebrow} title={title} />
      <p>{copy}</p>
    </section>
  );
}

export function FeatureLedger({
  items,
  staggered = false
}: {
  items: Feature[];
  staggered?: boolean;
}) {
  return (
    <section className="ledger section-pad" data-reveal>
      {items.map((item, index) => {
        const Icon = item.icon;
        return (
          <article
            className="ledger-row"
            data-reveal
            key={item.title}
            style={
              staggered
                ? ({ "--reveal-delay": `${Math.min(index * 120, 360)}ms` } as CSSProperties)
                : undefined
            }
          >
            <div className="ledger-icon">{Icon ? <Icon size={22} /> : null}</div>
            <div>
              <h3>{item.title}</h3>
              <p>{item.copy}</p>
            </div>
          </article>
        );
      })}
    </section>
  );
}

export function SplitProof({
  eyebrow,
  title,
  copy,
  items
}: TextBlock & { items: Feature[] }) {
  return (
    <section className="split-section section-pad" data-reveal>
      <div className="split-copy">
        <SectionHeading eyebrow={eyebrow} title={title} />
        <p>{copy}</p>
      </div>
      <div className="proof-list">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <article key={item.title}>
              <div className="proof-title">
                {Icon ? <Icon size={20} /> : null}
                <h3>{item.title}</h3>
              </div>
              <p>{item.copy}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function CapabilityStrip({ items }: { items: Capability[] }) {
  const [activeCapability, setActiveCapability] = useState(items[0]?.label ?? "");

  return (
    <section className="capability-strip section-pad" data-reveal>
      <Eyebrow>Core Capabilities</Eyebrow>
      <div className="capability-grid">
        {items.map((item) => (
          <button
            aria-controls={`capability-${slug(item.label)}`}
            aria-expanded={activeCapability === item.label}
            className={activeCapability === item.label ? "active" : undefined}
            key={item.label}
            onClick={() =>
              setActiveCapability((current) => (current === item.label ? "" : item.label))
            }
            type="button"
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="capability-detail">
        {items.map((item) => (
          <div
            aria-hidden={activeCapability !== item.label}
            className={activeCapability === item.label ? "active" : undefined}
            id={`capability-${slug(item.label)}`}
            key={item.label}
          >
            <strong>{item.label}</strong>
            <p>{item.summary}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function CTA({
  title,
  copy,
  label,
  href
}: {
  title: string;
  copy: string;
  label: string;
  href: string;
}) {
  return (
    <section className="cta section-pad narrow" data-reveal>
      <h2>{title}</h2>
      <p>{copy}</p>
      <Link className="button primary" to={href}>
        {label}
        <ArrowRight size={18} />
      </Link>
    </section>
  );
}

function slug(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
